// src/pages/invoicing/create-pi-items.js
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { Link } from "react-router-dom";
import Select from "react-select";
import API_BASE_URL from "../../config";
import { Modal, Button } from "react-bootstrap";

const CreateProformaItems = () => {
  const [lead, setLead] = useState(null);
  const [brands, setBrands] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [billing, setBilling] = useState(null);
  const [missingLeadFields, setMissingLeadFields] = useState({
    state: false,
  });

  useEffect(() => {
    document.title = "Create Proforma - Line Items | Webyappy";

    // 🔹 Load selected lead from localStorage
    const saved = localStorage.getItem("selected_proforma_lead");
    try {
      if (saved && saved !== "undefined") {
        const parsed = JSON.parse(saved);
        setLead(parsed);

        // 🧠 Set validation flag
        setMissingLeadFields({
          state: !parsed.state || parsed.state.trim() === "",
        });
      } else {
        console.warn("No valid lead found in localStorage.");
      }
    } catch (err) {
      console.error("Invalid JSON in selected_proforma_lead:", err);
    }

    // 🔹 Fetch Brands
    fetch(`${API_BASE_URL}/invoicing/get_brands`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setBrands(data.brands);
        } else {
          console.warn("Failed to load brands");
        }
      })
      .catch((err) => {
        console.error("Error fetching brands:", err);
      });

    // 🔹 Fetch Billing Address
    fetch(`${API_BASE_URL}/invoicing/get_billing_address`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setBilling(data.billing_address);
        } else {
          console.warn("No billing address found");
        }
      })
      .catch((err) => {
        console.error("Error fetching billing address:", err);
      });
  }, []);

  const [lineItems, setLineItems] = useState([
    { sac: "998314", title: "", gst: 18, amount: "" },
  ]);

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...lineItems];
    updatedItems[index][field] = value;
    setLineItems(updatedItems);
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { sac: "998314", title: "", gst: 18, amount: "" },
    ]);
  };

  const removeLineItem = (index) => {
    const updatedItems = [...lineItems];
    updatedItems.splice(index, 1);
    setLineItems(updatedItems);
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => {
      const amt = parseFloat(item.amount || 0);
      return sum + amt;
    }, 0);
  };

  const calculateTotalGST = () => {
    return lineItems.reduce((sum, item) => {
      const amt = parseFloat(item.amount || 0);
      const gst = parseFloat(item.gst || 0);
      return sum + (amt * gst) / 100;
    }, 0);
  };

  const calculateGrandTotal = () => {
    return calculateSubtotal() + calculateTotalGST();
  };

  const handleSubmitInvoice = async () => {
    if (!lead) return;

    if (missingLeadFields.state) return;

    // 🔴 Move this block up: Brand must be selected BEFORE setting isSubmitting
    if (!selectedBrand) {
      alert("Please select a brand before submitting.");
      return;
    }

    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item.title || item.title.trim() === "") {
        alert(`Please enter title for item ${i + 1}`);
        return;
      }
      if (!item.amount || isNaN(item.amount) || parseFloat(item.amount) <= 0) {
        alert(`Please enter valid amount for item ${i + 1}`);
        return;
      }
      if (item.gst === "" || isNaN(item.gst)) {
        alert(`Please enter GST% for item ${i + 1}`);
        return;
      }
      if (!item.sac || item.sac.trim() === "") {
        alert(`Please enter SAC code for item ${i + 1}`);
        return;
      }
    }

    // ✅ Only now: show loading state
    setIsSubmitting(true);

    try {
      // STEP 1: Create Proforma
      const createResponse = await fetch(
        `${API_BASE_URL}/invoicing/create_proforma_invoice`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            company_name: lead.company,
            address: `${lead.address}, ${lead.city}, ${lead.state} - ${lead.pincode}`,
            brand_id: selectedBrand?.value || null,
            to_company: lead.company,
            to_address: lead.address,
            to_city: lead.city,
            to_state: lead.state,
            to_pincode: lead.pincode,
            to_gstin: lead.gst_number ?? lead.gstNumber ?? lead.gstin ?? "",
          }),
        },
      );

      const createData = await createResponse.json();
      if (createData.status !== "success") {
        alert("Failed to create proforma invoice.");
        setIsSubmitting(false);
        return;
      }

      const pi_id = createData.pi_id;

      // STEP 2: Save Line Items
      const itemsToSave = lineItems.map((item) => ({
        pi_id,
        title: item.title,
        sac_code: item.sac,
        gst_percent: item.gst,
        amount: item.amount,
      }));

      await fetch(`${API_BASE_URL}/invoicing/add_proforma_line_items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(itemsToSave),
      });

      // STEP 3: Calculate GST
      let cgst = 0,
        sgst = 0,
        igst = 0;
      const totalGST = calculateTotalGST();
      if (
        billing?.state?.toLowerCase().trim() ===
        lead?.state?.toLowerCase().trim()
      ) {
        cgst = (totalGST / 2).toFixed(2);
        sgst = (totalGST / 2).toFixed(2);
      } else {
        igst = totalGST.toFixed(2);
      }

      const totals = {
        pi_id,
        subtotal: calculateSubtotal(),
        cgst,
        sgst,
        igst,
        grand_total: calculateGrandTotal(),
      };

      await fetch(`${API_BASE_URL}/invoicing/update_proforma_totals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(totals),
      });

      // STEP 4: Redirect
      window.location.href = `/pages/invoicing/pi-view/${pi_id}`;
    } catch (err) {
      console.error("Error submitting invoice:", err);
      alert("Something went wrong.");
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid px-3 py-3">
          <div className="row">
            {/* 📇 Left: Lead Card */}
            {lead ? (
              <div className="col-12 col-lg-4 mb-4">
                <h6 className="font-weight-bold text-dark mb-3">
                  Creating Proforma For:
                </h6>
                <div className="card card-custom shadow-sm px-4 py-4 position-relative h-100 w-100">
                  <div
                    className="position-absolute"
                    style={{ top: "1rem", right: "1rem" }}
                  >
                    <a
                      href={`/pages/crm/ViewLeadD/${lead.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="far fa-eye text-danger icon-lg"></i>
                    </a>
                  </div>

                  {/* Header: Profile + Info */}
                  <div className="d-flex flex-wrap align-items-center mb-3">
                    {lead.profile_pic ? (
                      <img
                        src={`${API_BASE_URL.replace("/index.php", "")}/${lead.profile_pic}`}
                        alt={lead.full_name}
                        className="rounded-circle mr-4"
                        style={{
                          width: "50px",
                          height: "50px",
                          objectFit: "cover",
                          border: "2px solid #a18dfa",
                          marginRight: "10px",
                        }}
                      />
                    ) : (
                      <div className="symbol symbol-50 mr-4">
                        <span
                          className="symbol-label rounded-circle text-white font-weight-bold d-flex align-items-center justify-content-center"
                          style={{
                            width: "50px",
                            height: "50px",
                            backgroundColor: "#a18dfa",
                            fontSize: "18px",
                            marginRight: "10px",
                          }}
                        >
                          {lead.full_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}

                    <div className="flex-grow-1 mt-3 mt-sm-0 ml-sm-3">
                      <h5
                        className="text-dark font-weight-bold mb-1"
                        style={{ fontSize: "15px" }}
                      >
                        {lead.full_name}
                      </h5>
                      <div className="text-dark" style={{ fontSize: "13px" }}>
                        <div className="mb-1">{lead.designation || "—"}</div>
                        <div className="font-weight-semibold">
                          {lead.company || "—"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact */}
                  <div
                    className="mb-2 text-dark"
                    style={{ fontSize: "13px", cursor: "pointer" }}
                    onClick={() => setShowContactModal(true)}
                  >
                    <i
                      className="fas fa-phone-alt text-primary mr-2"
                      style={{ marginRight: "10px" }}
                    ></i>
                    +91 {lead.mobile}
                    {lead.alt_mobile && ` | +91 ${lead.alt_mobile}`}
                  </div>

                  <div
                    className="mb-3 text-dark"
                    style={{ fontSize: "13px", cursor: "pointer" }}
                    onClick={() => setShowEmailModal(true)}
                  >
                    <i
                      className="fas fa-envelope text-primary mr-2"
                      style={{ marginRight: "10px" }}
                    ></i>
                    {lead.email} {lead.alt_email && ` | ${lead.alt_email}`}
                  </div>

                  {/* Tags */}
                  <div className="d-flex align-items-center flex-wrap mb-3">
                    {lead.funnel_stage && (
                      <span
                        className="badge badge-info font-weight-bold mr-2 mb-2 px-3 py-2"
                        style={{ fontSize: "12px", marginRight: "10px" }}
                      >
                        <i className="fas fa-filter mr-1"></i>{" "}
                        {lead.funnel_stage}
                      </span>
                    )}
                    {lead.priority && (
                      <span
                        className="badge badge-danger font-weight-bold mb-2 px-3 py-2"
                        style={{ fontSize: "12px" }}
                      >
                        {lead.priority} Priority
                      </span>
                    )}
                  </div>

                  {/* Meta Info */}
                  <div
                    className="d-flex flex-wrap align-items-center text-dark mb-3"
                    style={{ fontSize: "13px" }}
                  >
                    <div className="mr-4 mb-2 d-flex align-items-center">
                      <i
                        className="fas fa-user text-primary"
                        style={{ marginRight: "10px" }}
                      ></i>
                      <strong>Assigned to:</strong>&nbsp;
                      <span className="font-weight-bold">
                        {lead.assigned_to_name || "-"}
                      </span>
                    </div>
                    <div className="mb-2 d-flex align-items-center">
                      <i
                        className="fas fa-user-plus text-primary mr-1"
                        style={{ marginRight: "10px" }}
                      ></i>
                      {lead.created_by_name || "-"}
                      {lead.created_on && (
                        <>
                          &nbsp;|&nbsp;
                          {new Date(lead.created_on).toLocaleDateString(
                            "en-GB",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            },
                          )}{" "}
                          {new Date(lead.created_on).toLocaleTimeString(
                            "en-IN",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </>
                      )}
                    </div>
                    {/* ⚠️ Show validation message if state is missing */}
                    {missingLeadFields.state && (
                      <div className="alert alert-danger mt-3 small">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        Lead <strong>State</strong> is missing. GST calculation
                        will not work correctly. Please update the lead before
                        proceeding.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="col-12 col-lg-4 mb-4">
                <div className="card card-custom shadow-sm p-4 text-center text-muted">
                  No lead selected.
                </div>
              </div>
            )}

            {/* 📦 Right: Item Entry */}
            <div
              className="col-12 col-lg-8 mb-4"
              style={{ marginTop: window.innerWidth < 992 ? "2rem" : "0rem" }}
            >
              <h6 className="font-weight-bold text-dark mb-3">
                Proforma Item Entry
              </h6>
              <div className="card card-custom shadow-sm p-4">
                {/* Brand Select */}
                <div className="form-group">
                  <label className="small">
                    Brand<span className="text-danger">*</span>
                  </label>
                  <Select
                    className={`basic-single shadow-sm ${!selectedBrand && isSubmitting ? "border border-danger" : ""}`}
                    classNamePrefix="select"
                    options={brands.map((b) => ({
                      label: b.brand_name,
                      value: b.id,
                      logo: b.logo,
                      color: b.color_code,
                    }))}
                    placeholder="Select Brand"
                    isClearable
                    value={selectedBrand}
                    onChange={(selected) => setSelectedBrand(selected)}
                    formatOptionLabel={({ label, logo, color }) => (
                      <div className="d-flex align-items-center">
                        <div
                          className="border mr-2 d-flex align-items-center justify-content-center"
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "4px",
                            overflow: "hidden",
                            marginRight: "10px",
                          }}
                        >
                          <img
                            src={
                              logo?.startsWith("http")
                                ? logo
                                : `${API_BASE_URL.replace("/index.php", "")}/${logo}`
                            }
                            alt={label}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              display: "block",
                              padding: "4px",
                            }}
                          />
                        </div>
                        <span
                          style={{
                            color,
                            fontSize: "14px",
                            fontWeight: "Bold",
                          }}
                        >
                          {label}
                        </span>
                      </div>
                    )}
                  />
                </div>

                {/* Repeater rows */}
                {lineItems.map((item, index) => (
                  <div className="row mb-3 mt-3" key={index}>
                    <div className="col-sm-6 col-md-1 form-group mb-2">
                      <label className="small font-weight-bold">Sr. No.</label>
                      <input
                        type="text"
                        className="form-control"
                        value={index + 1}
                        readOnly
                      />
                    </div>
                    <div className="col-sm-6 col-md-2 form-group mb-2">
                      <label className="small font-weight-bold">SAC</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.sac}
                        onChange={(e) =>
                          handleLineItemChange(index, "sac", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-md-5 form-group mb-1">
                      <label className="small font-weight-bold">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Service Title"
                        value={item.title}
                        onChange={(e) =>
                          handleLineItemChange(index, "title", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-sm-6 col-md-2 form-group mb-2">
                      <label className="small font-weight-bold">GST%</label>
                      <input
                        type="number"
                        className="form-control"
                        value={item.gst}
                        onChange={(e) =>
                          handleLineItemChange(index, "gst", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-sm-6 col-md-2 form-group mb-2">
                      <label className="small font-weight-bold">Amount</label>
                      <input
                        type="text"
                        className="form-control"
                        value={item.amount}
                        onChange={(e) => {
                          const value = e.target.value;
                          // ✅ Allow only digits
                          if (/^\d*$/.test(value)) {
                            handleLineItemChange(index, "amount", value);
                          }
                        }}
                      />
                    </div>

                    <div
                      className="col-md-1 d-flex align-items-end"
                      style={{ paddingBottom: "12px" }}
                    >
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}

                {/* Add Item Button */}
                <div className="text-right mb-4">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={addLineItem}
                  >
                    <i className="fas fa-plus-circle mr-1"></i> Add Item
                  </button>
                </div>

                {/* Total Summary */}
                <div className="mt-4 border-top pt-4 text-right">
                  <div>
                    <strong>Subtotal:</strong> ₹{calculateSubtotal().toFixed(2)}
                  </div>
                  <div>
                    <strong>GST:</strong> ₹{calculateTotalGST().toFixed(2)}
                  </div>
                  <div className="font-weight-bold">
                    <strong>Grand Total:</strong> ₹
                    {calculateGrandTotal().toFixed(2)}
                  </div>
                </div>

                <div className="text-center mt-4">
                  {billing?.state && lead?.state && (
                    <div className="text-muted mt-2 text-right small">
                      Billing State: <strong>{billing.state}</strong> | Lead
                      State: <strong>{lead.state}</strong> —{" "}
                      {billing.state.toLowerCase() === lead.state.toLowerCase()
                        ? "CGST + SGST will apply"
                        : "IGST will apply"}
                    </div>
                  )}

                  <Button
                    className="btn btn-primary px-5 mt-3"
                    disabled={isSubmitting || missingLeadFields.state}
                    onClick={handleSubmitInvoice}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm mr-2" />
                        Submitting...
                      </>
                    ) : (
                      <>Submit Invoice</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
        centered
      >
        <div className="modal-content rounded-xl shadow p-4">
          <div className="modal-header py-2 border-0">
            <h5 className="modal-title font-weight-bold text-dark">
              Contact Options
            </h5>
            <button
              type="button"
              className="close"
              onClick={() => setShowContactModal(false)}
            >
              <i className="ki ki-close"></i>
            </button>
          </div>
          <div className="modal-body text-center py-3">
            {lead && (
              <>
                <div className="mb-4">
                  <div className="font-weight-bold text-dark mb-2">
                    +91 {lead.mobile}
                  </div>
                  <a
                    href={`tel:+91${lead.mobile}`}
                    className="btn btn-primary font-weight-bold mr-3 px-5"
                    style={{ marginRight: "10px" }}
                  >
                    <i className="fas fa-phone-alt mr-2"></i>Call
                  </a>
                  <a
                    href={`https://wa.me/91${lead.mobile}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-success font-weight-bold px-5"
                  >
                    <i className="fab fa-whatsapp mr-2"></i>WhatsApp
                  </a>
                </div>
                {lead.alt_mobile && (
                  <div>
                    <div className="font-weight-bold text-dark mb-2">
                      +91 {lead.alt_mobile}
                    </div>
                    <a
                      href={`tel:+91${lead.alt_mobile}`}
                      className="btn btn-primary font-weight-bold mr-3 px-5"
                    >
                      <i className="fas fa-phone-alt mr-2"></i>Call
                    </a>
                    <a
                      href={`https://wa.me/91${lead.alt_mobile}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-success font-weight-bold px-5"
                    >
                      <i className="fab fa-whatsapp mr-2"></i>WhatsApp
                    </a>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        centered
      >
        <div className="modal-content rounded-xl shadow p-4">
          <div className="modal-header py-2 border-0">
            <h5 className="modal-title font-weight-bold text-dark">
              Choose Email
            </h5>
            <button
              type="button"
              className="close"
              onClick={() => setShowEmailModal(false)}
            >
              <i className="ki ki-close"></i>
            </button>
          </div>
          <div className="modal-body text-center py-3">
            {lead?.email && (
              <a
                href={`mailto:${lead.email}`}
                className="btn btn-primary font-weight-bold mb-3 w-100"
              >
                <i className="fas fa-envelope mr-2"></i> {lead.email}
              </a>
            )}
            {lead?.alt_email && (
              <a
                href={`mailto:${lead.alt_email}`}
                className="btn btn-primary font-weight-bold w-100"
              >
                <i className="fas fa-envelope mr-2"></i> {lead.alt_email}
              </a>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CreateProformaItems;
